import { ApolloClient } from '@apollo/client'
import {
  GetTheatricalReleasesQuery,
  GetTheatricalReleasesQueryVariables,
  GetTheatricalReleasesWithCriticsQuery,
  GetTheatricalReleasesWithCriticsQueryVariables,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { DeepNonNullable } from '@/utils/types'
import { GET_THEATRICAL_RELEASES, GET_THEATRICAL_RELEASES_WITH_CRITICS } from './queries'

export type GetTheatricalReleasesItem = NonNullable<
  NonNullable<DeepNonNullable<GetTheatricalReleasesQuery, 2>['theatricalReleases']['theatricalRelease']>[number]
>

export async function getCurrentAndUpcomingTheatricalReleases(
  client: ApolloClient,
): Promise<GetTheatricalReleasesItem[]> {
  try {
    const releases = await getAllTheatricalReleases(client)
    const currentAndUpcoming = releases
      .filter(isDefined)
      .filter((release) => release?.region?.ticketsStatus === 'PRE_SALES' || release?.region?.ticketsStatus === 'LIVE')
    return currentAndUpcoming
  } catch (err) {
    logger().error('Error fetching current and upcoming theatrical releases', { error: err })
    return []
  }
}

export async function getAllTheatricalReleases(client: ApolloClient): Promise<GetTheatricalReleasesItem[]> {
  try {
    const result = await client.query<GetTheatricalReleasesQuery, GetTheatricalReleasesQueryVariables>({
      query: GET_THEATRICAL_RELEASES,
      variables: { input: {} },
      errorPolicy: 'all',
    })
    const releases = result.data?.theatricalReleases?.theatricalRelease || []
    return releases.filter(isDefined)
  } catch (err) {
    logger().error('Error fetching all theatrical releases', { error: err })
    return []
  }
}

/**
 * Critics score data extracted from theatrical release
 */
export interface TheatricalCriticsData {
  theatricalSlug: string
  projectSlug: string | null
  rottenTomatoesCritics: number | null
  rottenTomatoesAudience: number | null
  certifiedFresh: boolean
  verifiedHot: boolean
}

/**
 * Fetches all theatrical releases with critics scores
 * Returns a map keyed by projectSlug for easy lookup
 */
export async function getTheatricalReleasesWithCritics(
  client: ApolloClient,
): Promise<Map<string, TheatricalCriticsData>> {
  try {
    const result = await client.query<
      GetTheatricalReleasesWithCriticsQuery,
      GetTheatricalReleasesWithCriticsQueryVariables
    >({
      query: GET_THEATRICAL_RELEASES_WITH_CRITICS,
      variables: { input: {} },
      errorPolicy: 'all',
    })

    const releases = result.data?.theatricalReleases?.theatricalRelease || []
    const criticsMap = new Map<string, TheatricalCriticsData>()

    for (const release of releases) {
      if (!release?.projectSlug) continue

      // Find Rotten Tomatoes critics data (case-insensitive matching like useTheatricalRatings)
      const rtCritics = release.critics?.find((c) => c?.company?.toLowerCase() === 'rotten tomatoes')

      // Extract tomatometer (critics score) and audience score
      // Note: The score name is "Audience" not "Audience Score" per useTheatricalRatings
      const tomatometer = rtCritics?.scores?.find((s) => s?.name?.toLowerCase() === 'tomatometer')
      const audienceScore = rtCritics?.scores?.find((s) => s?.name?.toLowerCase() === 'audience')

      criticsMap.set(release.projectSlug, {
        theatricalSlug: release.theatricalSlug ?? '',
        projectSlug: release.projectSlug,
        rottenTomatoesCritics: tomatometer?.value ?? null,
        rottenTomatoesAudience: audienceScore?.value ?? null,
        certifiedFresh: rtCritics?.certifiedFresh ?? false,
        verifiedHot: rtCritics?.verifiedHot ?? false,
      })
    }

    return criticsMap
  } catch (err) {
    logger().error('Error fetching theatrical releases with critics', err)
    return new Map()
  }
}
